import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Observer } from "rxjs";
import { Router } from "@angular/router";

import { ByAssigned } from "../../../../model/filters/by-assigned";
import { MatchAny } from "../../../../model/filters/match.any";
import { Filter } from "../../../../model/filters/filter";
import { SingleChoice } from "../../../../model/singlechoice";
import { UsersService } from "../../../../services/users.service";
import { NavigationHelper } from "../../../../services/navigation.helper";
import { FilterService } from "../../../../services/filter.service";
import { Navigation } from "../../../../model/navigation";
import { Labeler } from "../../../../model/labeler";
import { NotComponent } from "../not/not.component";

@Component({
  selector: "by-assigned-form",
  templateUrl: "./by-assigned-form.component.html",
  styleUrl: "./by-assigned-form.component.css",
})
export class ByAssignedFormComponent implements OnInit {
  @Input() filter: ByAssigned = new ByAssigned();
  @Output() applyFilter = new EventEmitter<ByAssigned>();
  @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();

  byAssignedForm: FormGroup;
  
  editingIndex: number | null = null;
  assignedLabeler = new BehaviorSubject<string>("-");
  completionUrl = new BehaviorSubject<string>("tickets/-1/filter/by.assigned");
  currentId: number | null = null;
  byAssignedFilter: ByAssigned = new ByAssigned();
  @ViewChild(NotComponent) notComponent!: NotComponent;

  constructor(
    private fb: FormBuilder,
    private usersService: UsersService,
    private nav: NavigationHelper,
    private filterService: FilterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const existingFilters =
      this.filter instanceof MatchAny ? this.filter.filters : [this.filter];

    const userIdsFromFilter: number[] = existingFilters
      .filter((f): f is ByAssigned => f instanceof ByAssigned)
      .map((f) => f.id)
      .filter((id) => id !== undefined) as number[];

    const storedUserIds = localStorage.getItem("assignedUserIds");
    const userIdsFromStorage = storedUserIds
      ? (JSON.parse(storedUserIds) as number[])
      : [];

    const mergedIds = Array.from(
      new Set([...userIdsFromFilter, ...userIdsFromStorage])
    );

    this.initializeForm(mergedIds);
    if (mergedIds.length > 0) {
      this.initializeForm(mergedIds);
    } else {
      this.byAssignedForm = this.fb.group({
        assignedUsers: this.fb.array([
          this.fb.control(null, Validators.required),
        ]),
      });
    }

    if (mergedIds.length > 0) {
      this.currentId = mergedIds[0];
      this.assignedLabeler.next(this.usersService.labelForId(this.currentId));
    }
  }

  initializeForm(userIds: number[]): void {
    const controls = userIds.map((id) =>
      this.fb.control(id, Validators.required)
    );
    if (controls.length === 0)
      controls.push(this.fb.control(null, Validators.required));

    this.byAssignedForm = this.fb.group({
      assignedUsers: this.fb.array(controls),
    });

    this.currentId = controls[0]?.value ?? null;
    if (this.currentId !== null) {
      this.assignedLabeler.next(this.usersService.labelForId(this.currentId));
    }
  }

  restoreAssignedUsers(userIds: (number | null)[]): void {
    this.byAssignedForm.setControl("assignedUsers", this.fb.array([]));
    for (const id of userIds) {
      this.assignedUsers.push(this.fb.control(id, Validators.required));
    }
  }

  apply(): void {
    const ids: number[] = this.assignedUsers.value.filter(
      (id: number | null | undefined): id is number =>
        id !== null && id !== undefined
    );

    if (ids.length === 0) return;

    localStorage.setItem("assignedUserIds", JSON.stringify(ids));

    let finalFilter: Filter;
    if (ids.length === 1) {
      finalFilter = new ByAssigned(ids[0]);
    } else {
      finalFilter = new MatchAny(ids.map((id) => new ByAssigned(id)));
    }

    this.notComponent.filter = finalFilter;
    const modifiedFilter = this.notComponent.getModifiedFilter();

    this.applyFilter.emit(modifiedFilter);
    
  }

  onFilterChanged(finalFilter: Filter) {
    this.applyFilter.emit(finalFilter);
   
  }

  onAssignedChoice(assigned: number): void {
    if (
      this.editingIndex !== null &&
      this.assignedUsers.at(this.editingIndex)
    ) {
      this.assignedUsers.at(this.editingIndex).setValue(assigned);
    } else if (this.assignedUsers.length > 0) {
      this.assignedUsers.at(0).setValue(assigned);
    }

    const currentValues = this.assignedUsers.value.filter((id) => id !== null);
    localStorage.setItem("assignedUserIds", JSON.stringify(currentValues));

    this.currentId = assigned;

    this.router
      .navigate(["tickets/-1/filter/by.assigned/" + assigned])
      .then(() => this.nav.freshNavigation);
  }

  addAssignedField(): void {
    this.assignedUsers.push(this.fb.control(null, Validators.required));

    const currentValues = this.assignedUsers.value.filter((id) => id !== null);
    localStorage.setItem("assignedUserIds", JSON.stringify(currentValues));
  }

  removeField(index: number): void {
    if (this.assignedUsers.length > 1) {
      this.assignedUsers.removeAt(index);
      const currentValues = this.assignedUsers.value.filter((id) => id !== null);
      localStorage.setItem("assignedUserIds", JSON.stringify(currentValues));
    }
  }

  setEditingIndex(index: number): void {
    this.editingIndex = index;
    localStorage.setItem("editingAssignedIndex", index.toString());
  }

  relaySingleChoice(choice: SingleChoice) {
    this.nav.startChoice(choice);
  }

  get assignedUsers(): FormArray<FormControl> {
    return this.byAssignedForm.get("assignedUsers") as FormArray<FormControl>;
  }

  get assignedObserver(): Partial<Observer<number>> {
    return {
      next: (id) => {
        this.onAssignedChoice(id);
      },
    };
  }

  get fakeId(): number {
    return Navigation.NEW;
  }

  get assignedLabelerByService(): Labeler {
    return this.usersService;
  }
}
